/** Local search of the open map's node titles and notes. */
import { findTextMatches, highlightText, searchSnippet } from '../../../engine/core/text-search.js';
import { textFromHtml } from './search-content.js';
import { renderMarkdown } from '../views/notes/markdown.js';
import { escapeHtml } from '../../../engine/core/validation.js';
import { registerSearchNavigation } from './search-navigation.js';
import { t } from '../../../shared/i18n/index.js';

let deps = {};
let input, resultsEl, root, status, previous, next, clear;
let results = [];
let current = -1;
let timer = null;
let navigation = null;
let viewPointerHandler = null;
let scope = null;
const noteCache = new Map();

export function initSearch(callbacks) { deps = callbacks; }
function nodeNotes(node) {
  const raw = String(node.notes || '');
  const cached = noteCache.get(node.id);
  if (cached?.raw === raw) return cached.text;
  const text = raw ? textFromHtml(renderMarkdown(raw, escapeHtml)) : '';
  noteCache.set(node.id, {raw, text});
  return text;
}
export function search(query, {searchNotes = true, caseSensitive = false, limit = Infinity} = {}) {
  if (!String(query || '').trim()) return [];
  return (deps.getAll?.() || []).flatMap(node => {
    const name = String(node.name || node.text || t('common.untitled'));
    const titleMatches = findTextMatches(name, query, {caseSensitive});
    const notes = searchNotes ? nodeNotes(node) : '';
    const noteMatches = findTextMatches(notes, query, {caseSensitive});
    if (!titleMatches.length && !noteMatches.length) return [];
    return [{node, name, notes, matchType:titleMatches.length ? 'name' : 'notes',
      score:name.toLocaleLowerCase() === query.trim().toLocaleLowerCase() ? 100 : titleMatches.length ? 50 : 25}];
  }).sort((a,b) => b.score - a.score).slice(0,limit);
}
export const highlightMatches = highlightText;

function render() {
  if (!input || !resultsEl) return;
  const query = input.value.trim();
  const selected = results[current]?.node.id;
  results = search(query);
  current = selected ? results.findIndex(result => result.node.id === selected) : -1;
  status.textContent = query ? (results.length ? `${current + 1 || '–'} / ${results.length}` : t('views.search.noMatches')) : '';
  status.title = t('views.search.statusTitle');
  previous.disabled = next.disabled = !results.length;
  root.querySelector('.search-navigation').hidden = !query;
  clear.hidden = !query;
  resultsEl.hidden = !query;
  // Keep the menu bounded without truncating the keyboard navigation results.
  const start = Math.max(0, current - 20);
  const visible = results.slice(start, start + 80);
  resultsEl.innerHTML = !query ? '' : !results.length ? `<div class="search-empty">${t('views.search.empty')}</div>` : visible.map((result, offset) => {
    const {node, name, notes, matchType} = result;
    const index = start + offset;
    const path = (deps.getPath?.(node.id) || []).slice(0,-1).map(parent => parent.name || parent.text || t('common.untitled')).join(' › ');
    return `<button type="button" class="search-result${index === current ? ' current' : ''}" data-index="${index}" data-node-id="${escapeHtml(node.id)}" aria-current="${index === current}">
      <span class="search-result-content"><span class="search-result-name">${highlightText(name,query)}</span>
      <span class="search-result-path">${escapeHtml(path || t('views.search.mapRoot'))}</span>
      ${matchType === 'notes' ? `<span class="search-result-notes">${highlightText(searchSnippet(notes,query),query)}</span>` : ''}</span>
      <span class="search-result-kind">${matchType === 'notes' ? t('views.search.kindNote') : t('common.title')}</span></button>`;
  }).join('') + (results.length > visible.length ? `<div class="search-more">${t('views.search.more', { count: results.length })}</div>` : '');
}

function choose(index) {
  if (!results.length) return false;
  current = (index + results.length) % results.length;
  const id = results[current].node.id;
  render();
  navigation?.activate();
  deps.zoomTo?.(id);
  resultsEl.querySelector('.current')?.scrollIntoView({block:'nearest'});
  return true;
}
function returnToView() {
  const view = deps.getViewContainer?.();
  if (view?.isConnected) {
    if (!view.hasAttribute('tabindex')) view.tabIndex = -1;
    view.focus({preventScroll:true});
  } else if (root?.contains(document.activeElement)) {
    document.activeElement.blur();
  }
}
function acceptResult(id) {
  // A click can arrive before the input debounce. Resolve the visible node's
  // identity against fresh results, rather than an emptied/stale result index.
  clearTimeout(timer);
  render();
  const index = results.findIndex(result => result.node.id === id);
  if (index >= 0 && choose(index)) returnToView();
}
function move(direction) {
  clearTimeout(timer);
  render();
  choose(current < 0 ? (direction > 0 ? 0 : results.length - 1) : current + direction);
}
export function refreshSearch() {
  const nextScope = deps.getDocId?.();
  if (scope !== nextScope) { scope = nextScope; clearSearch(); }
  else if (input?.value.trim()) render();
}
export function setupSearchUI({input: inputEl, results: resultEl}) {
  input = inputEl; resultsEl = resultEl;
  if (!input || !resultsEl) return;
  root = input.closest('.sidebar-search');
  status = root.querySelector('[data-search-status]');
  previous = root.querySelector('[data-search-prev]');
  next = root.querySelector('[data-search-next]');
  clear = root.querySelector('[data-search-clear]');
  navigation?.dispose();
  navigation = registerSearchNavigation({primary:true,
    available:() => root.isConnected && !!root.getClientRects().length,
    contains:target => root.contains(target), hasQuery:() => !!input.value.trim(),
    focus:focusSearch, next:move});
  root.addEventListener('focusin', () => navigation.activate());
  // Canvas pan/drag handlers can prevent the browser's normal focus change.
  // An intentional click back in the view must release the search input too.
  if (viewPointerHandler) document.removeEventListener('pointerdown', viewPointerHandler, true);
  viewPointerHandler = event => {
    if (document.activeElement !== input || !deps.getViewContainer?.()?.contains(event.target)) return;
    if (event.target.closest?.('input, textarea, select, button, a, [contenteditable]')) return;
    returnToView();
  };
  document.addEventListener('pointerdown', viewPointerHandler, true);
  input.addEventListener('input', () => {
    clearTimeout(timer); current = -1; results = [];
    timer = setTimeout(render, 120);
  });
  input.addEventListener('keydown', event => {
    if (event.isComposing) return;
    if (['ArrowDown','ArrowUp','Enter','Escape'].includes(event.key)) {
      event.preventDefault(); event.stopPropagation();
      if (event.key === 'Escape') { clearSearch(); returnToView(); }
      else if (event.key === 'Enter') {
        clearTimeout(timer);
        render();
        // Arrow keys preview results; Enter accepts that result, without
        // advancing again or leaving subsequent letters in the search box.
        if (choose(current < 0 ? 0 : current)) returnToView();
      } else move(event.key === 'ArrowUp' ? -1 : 1);
    }
  });
  resultsEl.addEventListener('click', event => {
    const result = event.target.closest('[data-index]');
    if (result) acceptResult(result.dataset.nodeId);
  });
  resultsEl.addEventListener('keydown', event => {
    if (event.isComposing || event.ctrlKey || event.metaKey || event.altKey ||
        !['ArrowUp','ArrowDown','Enter',' ','Escape'].includes(event.key)) return;
    event.preventDefault(); event.stopPropagation();
    if (event.key === 'Escape') { clearSearch(); returnToView(); }
    else if (event.key === 'Enter' || event.key === ' ') {
      const result = event.target.closest('[data-index]');
      if (result) acceptResult(result.dataset.nodeId);
    }
    else { move(event.key === 'ArrowUp' ? -1 : 1); resultsEl.querySelector('.current')?.focus({preventScroll:true}); }
  });
  previous.addEventListener('click', () => move(-1));
  next.addEventListener('click', () => move(1));
  clear.addEventListener('click', () => { clearSearch(); focusSearch(); });
  scope = deps.getDocId?.();
  render();
}
export function clearSearch() {
  clearTimeout(timer); noteCache.clear(); results = []; current = -1;
  if (input) { input.value = ''; render(); }
}
export function focusSearch() {
  deps.showSidebar?.();
  input?.focus({preventScroll:true}); input?.select(); navigation?.activate();
}
export function isSearchActive() { return !!input?.value.trim(); }
